// src/context/CartContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';

type CartItem = {
  id: string;
  name: string;
  quantity: number;
  price: number;
  instructions?: string;
};

type CartContextType = {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  updateQuantity: (id: string, quantity: number) => void;
  updateInstructions: (id: string, instructions: string) => void;
  incrementItem: (id: string) => void;
  decrementItem: (id: string) => void;
  clearCart: () => void;
  totalPrice: number;
};

const CART_STORAGE_KEY = 'customer_cart';

const CartContext = createContext<CartContextType | undefined>(undefined);

// 🔁 Utility: persist to localStorage
const persistCart = (items: CartItem[]) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);

  // Load from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      if (stored) setItems(JSON.parse(stored));
    } catch (err) {
      console.error('Error loading cart:', err);
    }
  }, []);

  const addItem = (item: CartItem) => {
    if (item.quantity <= 0) return;

    setItems(prev => {
      const existing = prev.find(i => i.id === item.id);
      let updated;
      if (existing) {
        updated = prev.map(i =>
          i.id === item.id
            ? { ...i, quantity: i.quantity + item.quantity }
            : i
        );
      } else {
        updated = [...prev, item];
      }
      persistCart(updated);
      return updated;
    });
  };

  const updateQuantity = (id: string, quantity: number) => {
    setItems(prev => {
      const updated =
        quantity === 0
          ? prev.filter(i => i.id !== id)
          : prev.map(i =>
              i.id === id ? { ...i, quantity } : i
            );
      persistCart(updated);
      return updated;
    });
  };

  const updateInstructions = (id: string, instructions: string) => {
    setItems(prev => {
      const updated = prev.map(i =>
        i.id === id ? { ...i, instructions } : i
      );
      persistCart(updated);
      return updated;
    });
  };

  const incrementItem = (id: string) => {
    setItems(prev => {
      const updated = prev.map(item =>
        item.id === id ? { ...item, quantity: item.quantity + 1 } : item
      );
      persistCart(updated);
      return updated;
    });
  };

  const decrementItem = (id: string) => {
    setItems(prev => {
      const updated = prev
        .map(item =>
          item.id === id ? { ...item, quantity: item.quantity - 1 } : item
        )
        .filter(item => item.quantity > 0);
      persistCart(updated);
      return updated;
    });
  };

  const clearCart = () => {
    localStorage.removeItem(CART_STORAGE_KEY);
    setItems([]);
  };

  const totalPrice = items.reduce((acc, item) => acc + item.price * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateQuantity,
        updateInstructions,
        incrementItem,
        decrementItem,
        clearCart,
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
